import { useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Ban, FileDown, Undo2 } from "lucide-react";
import { useSales, useVoidSale, downloadSaleReceipt, PAYMENT_METHOD_LABEL } from "@/api/pos";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

/** Historial completo de ventas del POS (todas las sucursales/turnos, no solo el turno actual) — descarga de recibo PDF y anulación. Complementa la lista "Ventas de este turno" dentro del POS. */
export default function SalesHistoryPage() {
  const [status, setStatus] = useState<"" | "COMPLETADA" | "ANULADA">("");
  const [page, setPage] = useState(1);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canVoidSale = hasPermission("pos.delete");

  const { data, isLoading } = useSales({ status: status || undefined, page, pageSize: 20 });
  const voidSale = useVoidSale();
  const sales = data?.data ?? [];
  const meta = data?.meta;

  const handleVoid = async (id: string, saleNumber: number) => {
    if (!window.confirm(`¿Anular la venta #${saleNumber}? Esto revertirá el inventario descontado.`)) return;
    try {
      await voidSale.mutateAsync(id);
      toast.success(`Venta #${saleNumber} anulada.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Historial de ventas</h1>
        <p className="text-sm text-gray-500">Todas las ventas del POS, con descarga de recibo (PDF, no es factura electrónica DIAN).</p>
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <select
          className="input-field w-auto"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as typeof status);
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          <option value="COMPLETADA">Completada</option>
          <option value="ANULADA">Anulada</option>
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Venta</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Fecha</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Sucursal</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Método</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Total</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && sales.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                  No hay ventas con este filtro.
                </td>
              </tr>
            )}
            {sales.map((s) => (
              <tr key={s.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-600">#{s.saleNumber}</td>
                <td className="px-4 py-3 text-gray-600">{new Date(s.saleDate).toLocaleString("es-CO")}</td>
                <td className="px-4 py-3 text-gray-600">{s.branch?.name ?? "—"}</td>
                <td className="px-4 py-3 text-gray-600">{s.storeCustomer?.name ?? "—"}</td>
                <td className="px-4 py-3 text-gray-600">{PAYMENT_METHOD_LABEL[s.paymentMethod]}</td>
                <td className={`px-4 py-3 text-right ${s.status === "ANULADA" ? "text-red-600 line-through" : "font-medium text-gray-900"}`}>
                  {currency.format(Number(s.total))}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      s.status === "COMPLETADA" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                    }`}
                  >
                    {s.status === "COMPLETADA" ? "Completada" : "Anulada"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-3">
                    <button
                      type="button"
                      title="Descargar recibo (PDF)"
                      className="text-gray-400 hover:text-brand-600"
                      onClick={() => downloadSaleReceipt(s.id, s.saleNumber)}
                    >
                      <FileDown size={15} />
                    </button>
                    {s.status === "COMPLETADA" && (
                      <Link to={`/store/returns?sale=${s.saleNumber}`} title="Devolver productos" className="text-gray-400 hover:text-brand-600">
                        <Undo2 size={15} />
                      </Link>
                    )}
                    {s.status === "COMPLETADA" && canVoidSale && (
                      <button type="button" title="Anular venta" className="text-gray-400 hover:text-red-600" onClick={() => handleVoid(s.id, s.saleNumber)}>
                        <Ban size={15} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {meta && meta.totalPages && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm">
            <span className="text-gray-500">
              Página {meta.page} de {meta.totalPages} ({meta.total} ventas)
            </span>
            <div className="space-x-2">
              <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <button className="btn-secondary" disabled={!!meta.totalPages && page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
